/**
 * HATDEV-100772 — shared purchase amount / offer resolution and
 * Mixpanel `User_Purchased`-aligned PostHog property builders.
 *
 * Root cause for sub_total:0 / empty offer_id: `??` does not skip `""`, so an
 * empty `offers.price` or `offer_id` masked the real `int_price` / offer id.
 */

import { PosthogEventProperties } from "./posthog.service";

export interface PaymentPurchaseContext {
  purchaseDetail?: any;
  altprosubDetails?: any;
  preferredCurrency?: string;
  paymentMethod?: string;
  brand?: string;
}

export function isPresentScalar(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed !== "" && trimmed !== "null" && trimmed !== "undefined";
  }
  return true;
}

export function firstPresentString(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (!isPresentScalar(candidate)) {
      continue;
    }
    return String(candidate).trim();
  }
  return "";
}

/** Reject Firestore paths / refs that accidentally land in name-like fields. */
export function isUsableDisplayLabel(value: unknown): boolean {
  if (!isPresentScalar(value)) {
    return false;
  }
  const text = String(value).trim();
  if (text.startsWith("/")) {
    return false;
  }
  if (text.includes("general_modules/") || text.includes("prosub_id/")) {
    return false;
  }
  if (text.includes("/") && text.includes("details")) {
    return false;
  }
  return true;
}

export function firstPresentDisplayLabel(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (!isUsableDisplayLabel(candidate)) {
      continue;
    }
    return String(candidate).trim();
  }
  return "";
}

/**
 * Coerce a single amount candidate. Returns `null` when absent/invalid so
 * callers can try the next candidate (unlike Number("") === 0).
 */
export function coercePurchaseAmountValue(amount: unknown): number | null {
  if (amount === undefined || amount === null) {
    return null;
  }
  if (typeof amount === "string") {
    const trimmed = amount.trim();
    if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
      return null;
    }
    const cleaned = trimmed.replace(/[^0-9.-]/g, "");
    if (cleaned === "" || cleaned === "-" || cleaned === ".") {
      return null;
    }
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof amount === "object") {
    const map = amount as Record<string, unknown>;
    const mapped =
      map.GBP ??
      map.gbp ??
      map.USD ??
      map.EUR ??
      map.Value ??
      map.value ??
      Object.values(map).find((v) => {
        const n = Number(v);
        return Number.isFinite(n);
      });
    return coercePurchaseAmountValue(mapped);
  }
  const n = Number(amount);
  return Number.isFinite(n) ? n : null;
}

/**
 * Prefer the first positive amount so a leading `0` / free-tier placeholder
 * does not mask a later real `int_price` (e.g. GBP: 1). Falls back to the
 * first finite value (including 0) when nothing positive exists.
 */
export function firstPurchaseAmount(...candidates: unknown[]): number {
  let firstFinite: number | null = null;
  for (const candidate of candidates) {
    const n = coercePurchaseAmountValue(candidate);
    if (n === null) {
      continue;
    }
    if (n > 0) {
      return n;
    }
    if (firstFinite === null) {
      firstFinite = n;
    }
  }
  return firstFinite ?? 0;
}

export function readStoredPurchaseDetail(): any | null {
  try {
    if (typeof localStorage === "undefined") {
      return null;
    }
    const raw = localStorage.getItem("purchaseDetailData");
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function mergePaymentPurchaseContext(
  purchaseContext?: PaymentPurchaseContext | null,
  fallbacks?: {
    purchaseDetail?: any;
    altprosubDetails?: any;
  }
): PaymentPurchaseContext {
  const stored = readStoredPurchaseDetail();
  return {
    purchaseDetail:
      purchaseContext?.purchaseDetail ??
      fallbacks?.purchaseDetail ??
      stored ??
      undefined,
    altprosubDetails:
      purchaseContext?.altprosubDetails ??
      fallbacks?.altprosubDetails ??
      stored?.offers ??
      stored ??
      undefined,
    preferredCurrency: purchaseContext?.preferredCurrency,
    paymentMethod: purchaseContext?.paymentMethod,
    brand: purchaseContext?.brand,
  };
}

export function resolvePaymentAmountAndOffer(
  amount: unknown,
  offerId: unknown,
  currency: unknown,
  context?: PaymentPurchaseContext | null
): { subTotal: number; offerId: string; currency: string } {
  const purchase = context?.purchaseDetail;
  const alt = context?.altprosubDetails;
  const offers = purchase?.offers ?? alt;
  const currencyKey = firstPresentString(
    currency,
    offers?.currency,
    context?.preferredCurrency,
    "GBP"
  );

  const subTotal = firstPurchaseAmount(
    amount,
    alt?.price,
    purchase?.prize,
    purchase?.amount,
    offers?.price,
    offers?.int_price?.[currencyKey],
    offers?.int_price?.GBP,
    offers?.int_price,
    alt?.int_price?.[currencyKey],
    alt?.int_price?.GBP,
    alt?.int_price
  );

  const resolvedOfferId = firstPresentString(
    offerId,
    alt?.offerid,
    alt?.offer_id,
    purchase?.offer_id,
    offers?.offerid,
    offers?.offer_id,
    purchase?.offers?.offerid,
    purchase?.offers?.offer_id,
    alt?.Offer_ID,
    offers?.Offer_ID
  );

  return {
    subTotal,
    offerId: resolvedOfferId,
    currency: currencyKey || "GBP",
  };
}

function readStorage(key: string): string {
  try {
    if (typeof localStorage === "undefined") {
      return "";
    }
    return firstPresentString(localStorage.getItem(key));
  } catch {
    return "";
  }
}

/**
 * Build PostHog `payment_succeeded` props with Mixpanel `User_Purchased` parity
 * (same property names, including spaces) plus spike continuity fields.
 */
function asPosthogScalar(value: unknown): string | number | boolean | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  return String(value);
}

export function buildPaymentSucceededProps(input: {
  currency: string;
  subTotal: number;
  offerId: string;
  prosubId: string;
  customerStatus: unknown;
  customerCountry: unknown;
  customerPurchaseId: string;
  productType: unknown;
  customerId: string;
  context?: PaymentPurchaseContext | null;
}): PosthogEventProperties {
  const purchase = input.context?.purchaseDetail;
  const alt = input.context?.altprosubDetails;
  const offers = purchase?.offers ?? alt ?? {};

  // Lottery must be a human game label (e.g. EuroMillions), never a doc path /
  // generic offer title. Prefer dedicated lottery fields only.
  const lottery = firstPresentDisplayLabel(
    purchase?.real_name,
    offers?.real_name,
    alt?.real_name,
    offers?.lottery_name,
    alt?.lottery_name,
    purchase?.lottery_name,
    offers?.game_name,
    alt?.game_name,
    purchase?.lottery,
    offers?.lottery,
    alt?.lottery
  );
  const productName = firstPresentDisplayLabel(
    purchase?.product_name,
    offers?.product_name,
    offers?.offer_name,
    offers?.title,
    alt?.offer_name,
    alt?.title,
    offers?.name,
    alt?.name,
    lottery
  );
  const lotteryDraw = firstPresentString(
    offers?.draw_day,
    offers?.Draw_Days,
    purchase?.draw_day,
    alt?.draw_day
  );
  const jackpotValue = firstPresentString(
    offers?.jackpot,
    offers?.jackpot_value,
    offers?.Jackpot_Value,
    purchase?.jackpot,
    alt?.jackpot
  );
  const productCategory = firstPresentString(
    offers?.product_category,
    offers?.subscription_type,
    offers?.offer_type,
    "on-going"
  );
  const paymentMethod = firstPresentString(
    input.context?.paymentMethod,
    offers?.payment_type,
    purchase?.payment_method,
    "CheckOut"
  );
  const brand = firstPresentString(input.context?.brand, "App");
  const pointsBalanceRaw = firstPresentString(
    readStorage("points_balance"),
    readStorage("Points_Balance"),
    readStorage("user_points")
  );
  const pointsBalance =
    coercePurchaseAmountValue(pointsBalanceRaw) ??
    coercePurchaseAmountValue(0) ??
    0;
  const sqlUserId = firstPresentString(
    readStorage("sql_user_id"),
    readStorage("sql_id"),
    readStorage("customer_sql_id"),
    readStorage("user_sql_id")
  );
  const userState = firstPresentString(
    readStorage("user_state"),
    purchase?.user_state,
    input.customerStatus === "new" ? "free-reg" : ""
  );
  const gamificationRaw = firstPresentString(
    readStorage("gamification_status"),
    readStorage("Gamification_Status")
  );
  const gamificationStatus: boolean =
    gamificationRaw === "true" || gamificationRaw === "1";

  return {
    // Spike continuity (snake_case)
    currency: input.currency,
    sub_total: input.subTotal,
    offer_id: input.offerId,
    prosub_id: input.prosubId,
    customer_status: asPosthogScalar(input.customerStatus),
    customer_country: asPosthogScalar(input.customerCountry),
    customer_purchase_id: input.customerPurchaseId,
    product_type: input.productType != null ? String(input.productType) : undefined,
    c_customer_id: input.customerId,
    source: "gtm_payment_success",

    // Mixpanel User_Purchased parity (exact names)
    Gamification_Status: gamificationStatus,
    Brand: brand,
    "Offer ID": input.offerId,
    Jackpot_Value: jackpotValue,
    Lottery: lottery,
    Lottery_Draw: lotteryDraw,
    "Product Name": productName,
    Payment_Method: paymentMethod,
    Points_Balance: pointsBalance,
    Product: firstPresentString(offers?.product, purchase?.product, "offer"),
    Product_Category: productCategory,
    Prosub_ID: input.prosubId,
    User_State: userState,
    Value: input.subTotal,
    id: sqlUserId || input.customerId,
    sql_user_id: sqlUserId,
  };
}
